import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { Flex, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { routePaths } from 'router/route-paths';
import { useGetAdditionalCosts } from 'api/additional-cost/use-get-additional-costs';
import type { CargoRes, TAdditionalCostRes, TAdditionalCostType, TOrderRes, UserRes } from 'api/swagger';
import { useRoleGuard, UserRole } from 'shared/hooks/use-role-guard';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { AdditionalCostType } from 'types/additional-cost';
import { CURRENCY_SIGN, ECurrency } from 'types/currencies';
import { AdditionalCostControl } from 'components/common/additional-cost-control';
import { HiddenData } from 'components/hidden-data';
import { TripDeleteAllAdditionalCosts } from 'components/trips/trip-delete-all-additional-costs';
import type { ITripAdditionalCostsProps } from './types';

export const TripAdditionalCosts = (props: ITripAdditionalCostsProps) => {
  const { tripId } = props;
  const { t } = useTranslation(['additional-cost', 'order', 'cargo']);

  const { data: additionalCosts, isLoading } = useGetAdditionalCosts({ tripId });

  const isAllowFinancialInfo = useRoleGuard([
    UserRole.admin,
    UserRole.assistant,
    UserRole.transport_manager,
    UserRole.customer_manager,
    UserRole.storekeeper,
    UserRole.accountant,
  ]);

  const tableData = useTransformToTableData(additionalCosts || []);

  const columns: ColumnsType<TAdditionalCostRes> = [
    {
      title: t('order:order'),
      dataIndex: 'order',
      key: 'order',
      render: (order: TOrderRes) => {
        return <Link to={generatePath(routePaths.order, { orderId: String(order.id) })}>{order.compositeId}</Link>;
      },
    },
    {
      title: t('cargo:cargo'),
      dataIndex: 'cargo',
      key: 'cargo',
      render: (cargo: CargoRes) => {
        if (!cargo) return '–';
        return <Link to={generatePath(routePaths.cargo, { cargoId: String(cargo.id) })}>{cargo.compositeId}</Link>;
      },
    },
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: TAdditionalCostType) => {
        return <Tag>{AdditionalCostType[type]}</Tag>;
      },
    },
    {
      title: t('cost'),
      dataIndex: 'value',
      key: 'value',
      render: (value: number, { valueInDefault, currency }: TAdditionalCostRes) => {
        if (!isAllowFinancialInfo) return <HiddenData />;

        const isDefaultCurrency = currency === ECurrency.EUR;
        return (
          <>
            <div>
              {isDefaultCurrency
                ? `${value} ${CURRENCY_SIGN[ECurrency.EUR]}`
                : `≈ ${valueInDefault} ${CURRENCY_SIGN[ECurrency.EUR]}`}
            </div>
            {!isDefaultCurrency ? (
              <div>
                <Typography.Text type='secondary'>
                  {value} {CURRENCY_SIGN[currency]}
                </Typography.Text>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      title: t('user'),
      dataIndex: 'user',
      key: 'user',
      render: (user: UserRes) => {
        return user.name;
      },
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => {
        return <AdditionalCostControl tripId={tripId} additionalCost={record} />;
      },
    },
  ];

  return (
    <Flex gap='middle' vertical>
      {tableData.length > 0 && <TripDeleteAllAdditionalCosts tripId={tripId} />}
      <Table loading={isLoading} columns={columns} dataSource={tableData} pagination={false} />
    </Flex>
  );
};
